import { observeCameraRemoval } from '#~/device/remove-trace.js';
import { isCurrentSnapshotLoad, loadSnapshot } from '#~/agent/broker.js';
// Spec 4 (D1): per-wait attribution for the room route.
import { timeBootHop } from '#~/logger/forward.js';
import { markRendererMilestone } from '#~/boot/renderer-startup.js';
import { isApiReachable, retryWhenReachable } from '#~/boot/retry-when-reachable.js';
import type { Handlers, Proxy, Transaction } from '#~/agent/index.js';
import { ErrorIds, broker as proxyBroker } from '#~/agent/index.js';
import { fetchAgentsForRoomAwaitingExpected } from '#~/api/agent/index.js';
import { toIdFromHex, type ByteId } from '#~/api/index.js';
import { useAuth0 } from '#~/auth/index.js';
import type { AutoTrackingState } from '#~/auto-tracking/index.js';
import { transformAutoTrackingReservations } from '#~/auto-tracking/index.js';
import { Slicer, useDepot, useProducer } from '#~/depot/hooks.js';
import { depot } from '#~/depot/index.js';
import { broker as liveKitBroker } from '#~/livekit/index.js';
import type { LiveKit } from '#~/livekit/index.js';
import { logger } from '#~/logger/index.js';
import { nats } from '#~/nats/index.js';
import * as Notifications from '#~/notifications/index.js';
import type { NewtsConnection } from '#~/newts/connection.js';
import { StudioNotFound } from '../components/not-found.js';
import { Context } from './context.js';
import { TransitoryLivenessWatcher } from './transitory-liveness-watcher.js';
import type { Props } from './types.js';
import { handleProxyStateError } from './proxy-state-error.js';
import { initialize, synchronize, synchronizeAutoTrackingAnnotations } from './utils.js';
import type { Annotation } from '@studio/design-system/video-annotation';
import { unwrap } from '@studio/js-utils/async';
import { generateId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import { setPendingDevice, type Device } from '@studio/models/device';
import { toEmbeddedShadowDeviceId } from '#~/device/embedded-shadow-id.js';
import { useAsyncEffect } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { castDraft, freeze } from 'immer';
import { throttle } from 'lodash-es';
import type { ReactElement } from 'react';
import { useRef, useState } from 'react';

//import { liveKitDeviceResolver } from './extern.js';

// Room resources span the /boot → room route handoff. Defer teardown across the gap
// and retarget state-sync handlers to the adopting Provider.

// Proxies capture these handlers once; adoption retargets their callbacks to the live Provider.
interface HandlerDelegate {
  readonly handlers: Handlers;
  retarget: (target: T_Maybe<Handlers>) => void;
}

/** Reference-counted ownership record for one room. */
interface RoomOwnership {
  accountId: T_Maybe<string>;
  count: number;
  readonly delegate: HandlerDelegate;
  // Shared across Provider adoption; abort only when the room resources are torn down.
  readonly controller: AbortController;
  teardown?: () => Promise<void>;
  // `| undefined` is required to clear it under exactOptionalPropertyTypes.
  grace?: ReturnType<typeof setTimeout> | undefined;
}

// Allow navigation and token/agent loading to hand off ownership before teardown.
// A macrotask timer survives microtask draining and is cancelled by synchronous retain.
export const HANDOFF_GRACE_MS = 3_000;

const roomOwners = new Map<string, RoomOwnership>();

const makeHandlerDelegate = (): HandlerDelegate => {
  let target: T_Maybe<Handlers>;
  return {
    retarget: (next) => {
      target = next;
    },
    // Each method forwards to the CURRENT owner's closure, so retargeting is all
    // an adopting owner needs — the proxy keeps calling this same object.
    handlers: {
      handleAutoTrackingAnnotationMutation: (agentId, cameraId, annotations) =>
        target?.handleAutoTrackingAnnotationMutation(agentId, cameraId, annotations),
      handleAutoTrackingMutation: (agentId, cameraId, state) =>
        target?.handleAutoTrackingMutation(agentId, cameraId, state),
      handleError: (error) => target?.handleError(error),
      handleTransaction: (tx, agentId) => target?.handleTransaction(tx, agentId)
    }
  };
};

const runTeardown = (owner: RoomOwnership): Promise<void> => {
  // Grace expiry and room/account changes retire the shared lifetime before resource cleanup.
  owner.controller.abort();
  const done = (owner.teardown?.() ?? Promise.resolve()).catch((cause) => {
    logger.error(cause);
  });
  return done;
};

/** Tear a room down immediately (account switch / roomId change), not on grace. */
const flushOwner = (roomId: string, owner: RoomOwnership): Promise<void> => {
  if (owner.grace) {
    clearTimeout(owner.grace);
    owner.grace = undefined;
  }
  if (roomOwners.get(roomId) === owner) roomOwners.delete(roomId);
  return runTeardown(owner);
};

// Adopt live resources only for the same account; an account change tears down first.
export const retainRoom = (
  roomId: string,
  accountId: T_Maybe<string>
): {
  adopted: boolean;
  delegate: HandlerDelegate;
  signal: AbortSignal;
  priorTeardown: Promise<void>;
} => {
  const existing = roomOwners.get(roomId);

  // Same room, same account: the /boot -> room handoff — adopt the live
  // resources and cancel the pending teardown. The adopting owner shares the
  // existing controller's signal, so the proxies it adopts keep their live lifetime.
  if (existing && existing.accountId === accountId) {
    if (existing.grace) {
      clearTimeout(existing.grace);
      existing.grace = undefined;
    }
    existing.count += 1;
    return {
      adopted: true,
      delegate: existing.delegate,
      signal: existing.controller.signal,
      priorTeardown: Promise.resolve()
    };
  }

  // Same room id, different account: a real account switch. Tear the old owner
  // down now so its resources are released before the new owner recreates them;
  // the broker mutex serialises destroy-before-create. Callers on the SAME
  // Provider instance await this; a fresh instance relies on the mutex.
  let priorTeardown: Promise<void> = Promise.resolve();
  if (existing) {
    priorTeardown = flushOwner(roomId, existing);
  }

  const owner: RoomOwnership = {
    accountId,
    count: 1,
    controller: new AbortController(),
    delegate: makeHandlerDelegate()
  };
  roomOwners.set(roomId, owner);
  return {
    adopted: false,
    delegate: owner.delegate,
    signal: owner.controller.signal,
    priorTeardown
  };
};

/**
 * Release ownership of a room. The last owner's release arms the grace window;
 * only its expiry (with the count still at zero) runs the teardown. A new owner
 * that retains within the window cancels it and adopts.
 */
export const releaseRoom = (
  roomId: string,
  accountId: T_Maybe<string>,
  teardown: () => Promise<void>
): void => {
  const owner = roomOwners.get(roomId);
  // Our ownership was already superseded by a newer account: tear down only what
  // we created, without touching the newer owner's live resources.
  if (!owner || owner.accountId !== accountId) {
    void teardown().catch((cause) => {
      logger.error(cause);
    });
    return;
  }
  owner.teardown = teardown;
  owner.count -= 1;
  if (owner.count > 0) return;
  owner.grace = setTimeout(() => {
    if (roomOwners.get(roomId) === owner && owner.count <= 0) {
      roomOwners.delete(roomId);
      void runTeardown(owner);
    }
  }, HANDOFF_GRACE_MS);
};

/**
 * Tear a predecessor room owned by THIS Provider instance down now (roomId
 * change) instead of waiting out its grace window.
 */
const flushOwnedRoom = (roomId: string, accountId: T_Maybe<string>): Promise<void> => {
  const owner = roomOwners.get(roomId);
  if (!owner || owner.accountId !== accountId || owner.count > 0) return Promise.resolve();
  return flushOwner(roomId, owner);
};

/** Room `<Provider>`. */
export const Provider = ({ children, options, roomId }: Props): ReactElement => {
  const [error, setError] = useState<T_Maybe<Error>>();
  // Bumping this re-runs the room setup effect (WAN-outage auto-retry below).
  const [bootAttempt, setBootAttempt] = useState(0);
  // Retries still allowed after the API answers again; armed by an outage.
  const wanRetriesRef = useRef(0);
  const cleanupRef = useRef<() => void>(() => {});
  // The room + account this instance currently owns, so a same-instance roomId
  // or account change tears the predecessor down deterministically.
  const ownedRef = useRef<T_Maybe<{ roomId: string; accountId: T_Maybe<string> }>>(undefined);
  const accountId = useDepot((state) => state.account?.id);
  const producer = useProducer();
  const { getAccessTokenSilently } = useAuth0();

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      const { appId } = depot.getState();

      // Retain before awaiting so route handoff cancels the outgoing Provider’s grace timer.
      const previous = ownedRef.current;
      ownedRef.current = { roomId, accountId };
      const retention = retainRoom(roomId, accountId);

      let priorTeardown: Promise<void> = Promise.resolve();
      // Same Provider instance switching account on the same room: serialise the
      // new owner behind the old owner's teardown — an account change is always a
      // real teardown, never an adopt across accounts.
      if (previous && previous.roomId === roomId && previous.accountId !== accountId) {
        priorTeardown = retention.priorTeardown;
      }
      // Same Provider instance moving to a different room: tear the old room down
      // now (its cleanup released it into the grace window) and serialise behind
      // it, so children render through the gap while the new room builds.
      if (previous && previous.roomId !== roomId) {
        priorTeardown = Promise.all([
          priorTeardown,
          flushOwnedRoom(previous.roomId, previous.accountId)
        ]).then(() => {});
      }

      let retired = false;
      let proxies: Record<string, Proxy> | undefined;
      let liveKit: LiveKit | undefined;
      let proxyCreation: Promise<Record<string, Proxy>> | undefined;
      let liveKitCreation: Promise<LiveKit> | undefined;
      const disposers: (() => void)[] = [];
      const current = (): boolean =>
        !retired && !signal.aborted && depot.getState().account?.id === accountId;
      cleanupRef.current = () => {
        if (retired) return;
        retired = true;
        disposers.forEach((dispose) => dispose());
        // Defer resource and depot-row removal together so a same-room handoff adopts both.
        releaseRoom(roomId, accountId, async () => {
          const proxyCleanup = proxyCreation
            ? proxyBroker.destroyRoom(proxyCreation)
            : Promise.resolve();
          const liveKitCleanup = liveKitCreation
            ? liveKitBroker.destroy(roomId)
            : Promise.resolve();
          await Promise.all([proxyCleanup, liveKitCleanup]).catch((cause) => {
            logger.error(cause);
          });
          producer((draft) => {
            delete draft.rooms[roomId];
          });
        });
      };
      setError(undefined);
      yield* unwrap(priorTeardown);
      if (!current()) return;

      // Retarget the proxy’s stable handlers to this Provider’s closures.
      const ownHandlers: Handlers = {
        handleAutoTrackingAnnotationMutation: (
          agentId: Agent['id'],
          cameraId: ByteId,
          annotations: T_Maybe<Annotation[]>
        ) => {
          if (current()) synchronizeAutoTrackingAnnotations(agentId, cameraId, annotations);
        },

        handleAutoTrackingMutation: (
          _agentId: Agent['id'],
          _cameraId: ByteId,
          _state: T_Maybe<AutoTrackingState>
        ) => {
          // synchronizeAutoTracking(agentId, cameraId, state);
        },

        handleError: (error_: Error): void => {
          if (current()) Notifications.raiseError(error_);
        },

        handleTransaction: throttle(
          (tx: Transaction, agentId: Agent['id']) => {
            if (current()) {
              synchronize(agentId, roomId, tx);
              observeCameraRemoval(agentId, 'ui.membership', (id) => ({
                roomId,
                present: !!depot.getState().rooms[roomId]?.devices[id as Device['id']]
              }));
            }
          },
          0,
          { leading: true, trailing: true }
        )
      };
      retention.delegate.retarget(ownHandlers);

      try {
        // Spec 4 (D1): the room route (including a direct /room/:id refresh) never
        // runs the root bootstrap, so THIS is the sequence that owns a warm
        // refresh. Each wait is measured separately; `createRoom` returns as soon
        // as the proxies exist while their runners continue in the background, so
        // its span is deliberately not charged the full proxy start.
        const token = yield* unwrap(
          timeBootHop('room.get-token', getAccessTokenSilently(), 'room')
        );

        if (!current()) return;

        // Waits (bounded) for a camera the claim screen just linked here, so the
        // proxies below include it even when the registry read lags the link.
        const agents = yield* unwrap(
          timeBootHop(
            'room.fetch-agents',
            fetchAgentsForRoomAwaitingExpected(roomId, token),
            'room'
          )
        );
        if (!current()) return;

        // The status query can still hold its pre-claim result. Install verified
        // room membership before a new proxy publishes its camera, or that
        // camera's menu treats missing embedded metadata as an ordinary source.
        // Per think-in-systems (ai.knowledge/global): preserve newer depot data.
        producer((draft) => {
          if (signal.aborted) return;
          for (const agent of Object.values(agents)) {
            draft.agents[agent.id] ??= {
              ...agent,
              status: agent.embedded ? 'offline-embedded' : 'offline'
            };
          }
        });

        // Construct the agent proxy. The stable delegate — not `ownHandlers` — is
        // what the proxies capture, so an adopting owner only retargets it.
        proxies = yield* unwrap(
          timeBootHop(
            'room.create-proxies',
            (proxyCreation = proxyBroker.createRoom(agents, {
              appId,
              handlers: retention.delegate.handlers,
              options,
              roomId,
              // Registry-owned lifetime signal, NOT this effect's signal: a slow boot
              // Provider that aborts mid-setup must not retire proxies the room adopts.
              // Per rule: settle-every-terminal-branch (ai.knowledge/global).
              signal: retention.signal,
              tokenGenerator: getAccessTokenSilently
            })),
            'room'
          )
        );

        if (!current()) return;

        for (const proxy of Object.values(proxies!)) {
          // Clear any connection error.
          Notifications.hide(`${ErrorIds.ConnectionFailed}-${proxy.id}`);

          if (proxy.embedded) {
            const fn = async () => {
              const id = toEmbeddedShadowDeviceId(proxy.id);

              setPendingDevice({
                id,
                name: proxy.name ?? 'Loading...',
                embedded: true,
                agentId: proxy.id
              });

              const load = loadSnapshot(proxy, token);
              const snapshot = await load;
              if (!current() || !isCurrentSnapshotLoad(proxy, load)) return;
              const deviceInSnapshot = snapshot?.control?.studio?.device?.[id];

              setPendingDevice({
                id,
                name: deviceInSnapshot?.name ?? proxy.name ?? 'Loading...',
                embedded: true,
                agentId: proxy.id
              });
            };

            // Snapshot failure must preserve the pending source, not escape as an unhandled rejection.
            void fn().catch((cause: unknown) => {
              if (cause instanceof DOMException && cause.name === 'AbortError') return;
              logger.warning(cause as Error);
            });
          }

          const onConnected = async (): Promise<void> => {
            if (!current()) return;
            const agentId = proxy.id;
            const reservations = proxy.state.control?.studio?.autoTrackingReservation ?? {};

            const conn = nats.agentConnection(agentId) as NewtsConnection;
            const canPing = await conn.pingAgent();

            if (!current()) return;
            if (!canPing) {
              onError(new Error('Ping failed'));
              return;
            }

            const autoTrackingInstances = freeze(
              transformAutoTrackingReservations(reservations, proxy)
            );

            // Add the proxy to the depot.
            producer((draft) => {
              draft.autoTrackingInstances[agentId] = autoTrackingInstances;

              // A pending ping may finish after the agent was removed. Only
              // promote membership that still exists; never recreate it here.
              if (draft.agents[agentId] !== undefined) {
                draft.agents[agentId]!.status = 'online';
              }
            });
          };

          const onError = async (_error: Error): Promise<void> => {
            const conn = nats.agentConnection(proxy.id) as NewtsConnection;
            await handleProxyStateError(proxy, conn, (recipe) => {
              if (!current()) return;
              producer((draft) => {
                recipe(draft);
              });
            });
          };

          disposers.push(proxy.on('state.started', onConnected), proxy.on('state.error', onError));

          if (proxy.isStarted) {
            void onConnected().catch(ownHandlers.handleError);
          }
        }

        // Initialize the room
        const room_ = initialize(proxies!, roomId);

        // Add the room to the depot.
        producer((draft) => {
          draft.rooms[roomId] = room_;
        });
        markRendererMilestone('room.published');

        //const conn = desktopAgent?.connection;
        const localEncoderIds: Uint8Array[] = Object.values(proxies!).map(
          (proxy) => toIdFromHex(proxy.id)!
        );

        const mode = 'cloud';

        // Construct the LiveKit connection. `token` is the one-shot credential
        // for the FIRST mint; `getAuthToken` is the live accessor the broker
        // closes over for later re-mints. Without it a cloud rebuild (LiveKit
        // Disconnected, or a room rebuilt after power:resume) would re-mint with
        // the Auth0 token captured at boot, which has usually expired while the
        // machine slept — the rebuild then fails and the room never recovers.
        // Mirrors routes/room/agent-lookup-and-wait.tsx.
        // Per rule: code-comments-required (ai.knowledge/global).
        liveKit = yield* unwrap(
          timeBootHop(
            'room.livekit-create',
            (liveKitCreation = liveKitBroker.create(roomId, {
              appId,
              // Registry-owned lifetime signal (see createRoom above): the LiveKit
              // connection outlives the boot Provider so the handoff never drops it.
              signal: retention.signal,
              localEncoderIds,
              mode,
              token,
              getAuthToken: () => getAccessTokenSilently()
            })),
            'room'
          )
        );

        // Watch the LiveKit connection for track changes.
        if (!current()) return;
        wanRetriesRef.current = 0;
        disposers.push(
          liveKit!.on('changed', () => {
            if (!current()) return;
            producer((draft) => {
              /*
               * We use unique UUID a change in the depot whenever the set of LiveKit
               * tracks change. This allows us to bridge the depot state and the LiveKit state.
               */
              castDraft(new Slicer(draft).room(roomId)).liveKitSync = generateId();
            });
          })
        );

        /*
         * Debug voyeur token - https://livekit.io/playground
         *
         * import { createToken } from '#~/api/livekit/index.js';
         * import { fetchAgentsForRoom } from '#~/api/agent/index.js';
         * import { isConnected } from '@studio/models/control/movement';
         */
      } catch (error_) {
        if (!signal.aborted) {
          setError(error_ as Error);
          // A room setup that fails while the cloud API is unreachable is not a
          // verdict on the room: it is usually the cloud LiveKit token mint
          // failing with the WAN down. Nothing re-ran this effect, so Studio
          // stayed on "Studio not found" after the uplink came back.
          // Re-run the setup once the API answers. Cloud NATS can lag the API,
          // so an outage also allows a few more retries while reachable; an
          // error seen while online with no outage stays final, as before.
          // Per rule: reference-rules-in-comments (ai.knowledge/global).
          void isApiReachable().then((reachable) => {
            if (!current()) return;
            if (!reachable) wanRetriesRef.current = 3;
            else if (wanRetriesRef.current > 0) wanRetriesRef.current -= 1;
            else return;
            disposers.push(
              retryWhenReachable(() => {
                if (current()) setBootAttempt((attempt) => attempt + 1);
              })
            );
          });
        }
      }
    },
    (): void => cleanupRef.current(),
    [roomId, accountId, bootAttempt]
  );

  if (error) {
    return <StudioNotFound />;
  }

  return (
    <Context.Provider value={roomId}>
      {/* Proactive ONVIF placeholder liveness (spec §7 / §17.2#7). */}
      <TransitoryLivenessWatcher />
      {children}
    </Context.Provider>
  );
};
